import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiUploadCloud, FiDownload, FiPlay, FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';
import Button from '../common/Button';
import * as migrationService from '../../services/api/migrationService';

/**
 * Settings → Migration: go-live import of the operational A26 Excel workbook.
 * Flow: upload & parse → verify/correct in the tables below → download review
 * CSVs → dry-run import → real import. Re-running with a newer workbook only
 * applies deltas (the import is idempotent).
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Intro = styled.p`
  color: var(--ve-ink-700);
  margin: 0;
  max-width: 70ch;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const StatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Stat = styled.div`
  background: var(--surface-subtle, #f7f7f7);
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.1));
  border-radius: var(--radius-content, 6px);
  padding: 8px 14px;
  font-size: 0.85rem;
  color: var(--ve-ink-700);
  b {
    display: block;
    font-size: 1.15rem;
    color: var(--ve-ink-800);
  }
`;

const SectionTitle = styled.h3`
  margin: 10px 0 0;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--ve-ink-800);
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.1));
  border-radius: var(--radius-content, 6px);
  max-height: 420px;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  th {
    position: sticky;
    top: 0;
    background: var(--surface-subtle, #f7f7f7);
    color: var(--ve-ink-600);
    text-align: left;
    padding: 8px 10px;
    white-space: nowrap;
    border-bottom: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.1));
  }
  td {
    padding: 6px 10px;
    border-bottom: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.06));
    color: var(--ve-ink-700);
    vertical-align: top;
    white-space: nowrap;
  }
`;

const Cellput = styled.input`
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.15));
  border-radius: var(--radius-control, 4px);
  padding: 3px 6px;
  font: inherit;
  width: ${({ $w }) => $w || '130px'};
  &:focus {
    outline: 2px solid var(--act-primary, #17627f);
    outline-offset: 0;
  }
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  background: ${({ $tone }) =>
    $tone === 'ok' ? 'var(--status-approved-plate, #f2f7e6)' : $tone === 'bad' ? 'var(--status-danger-plate, #fdecec)' : 'var(--status-warning-plate, #fdf4e6)'};
  color: ${({ $tone }) =>
    $tone === 'ok' ? 'var(--status-approved-ink, #5d7c1e)' : $tone === 'bad' ? 'var(--status-danger-ink, #c3251d)' : 'var(--status-warning-ink, #9a6a10)'};
`;

const WarnList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: var(--status-warning-ink, #9a6a10);
  font-size: 0.82rem;
  max-height: 180px;
  overflow-y: auto;
`;

const Note = styled.div`
  font-size: 0.78rem;
  color: var(--ve-ink-600);
  max-width: 70ch;
`;

const Alert = styled.div`
  border-radius: var(--radius-content, 6px);
  padding: 10px 14px;
  font-size: 0.85rem;
  background: ${({ $tone }) => ($tone === 'error' ? 'var(--status-danger-plate, #fdecec)' : 'var(--status-success-plate, #eaf7f4)')};
  color: ${({ $tone }) => ($tone === 'error' ? 'var(--status-danger-ink, #c3251d)' : 'var(--status-success-ink, #1d8e7c)')};
`;

const toHmm = (minutes) => `${Math.floor(minutes / 60)}:${String(Math.round(minutes) % 60).padStart(2, '0')}`;

const CSV_KINDS = ['customers', 'employees', 'hours', 'monthly', 'pipeline', 'warnings'];

const MigrationPanel = () => {
  const fileRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [billingMode, setBillingMode] = useState('historic-all');
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const [batches, setBatches] = useState([]);

  const refreshBatches = async () => {
    try {
      setBatches(await migrationService.listBatches());
    } catch (e) {
      /* history is optional */
    }
  };
  useEffect(() => {
    refreshBatches();
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setParsing(true);
    setError(null);
    setReport(null);
    try {
      const { batchId: id, payload: p } = await migrationService.parseExcel(file);
      // include flags default to true — the tables below can exclude rows.
      p.customers.forEach((c) => { if (c.include === undefined) c.include = true; });
      p.employees.forEach((emp) => { if (emp.include === undefined) emp.include = true; });
      setBatchId(id);
      setPayload(p);
      setNotice(`Parsed "${file.name}" — review the data below, download the CSVs, then run a dry-run import.`);
      refreshBatches();
    } catch (err) {
      setError(err.message);
    } finally {
      setParsing(false);
    }
  };

  const patchCustomer = (key, field, value) => {
    setPayload((p) => ({
      ...p,
      customers: p.customers.map((c) => (c.key === key ? { ...c, [field]: value } : c)),
    }));
  };
  const patchEmployee = (key, field, value) => {
    setPayload((p) => ({
      ...p,
      employees: p.employees.map((emp) => (emp.key === key ? { ...emp, [field]: value } : emp)),
    }));
  };

  const runImport = async () => {
    if (!payload) return;
    if (!dryRun) {
      const go = window.confirm(
        'This will WRITE the reviewed data into this Tempus instance (customers, employees, assignments and hours). Continue?'
      );
      if (!go) return;
    }
    // Rate cells are edited as raw text ("12,5") — normalize to numbers here.
    const cleanPayload = {
      ...payload,
      employees: payload.employees.map((emp) => {
        if (emp.rateEur == null || emp.rateEur === '') return { ...emp, rateEur: null };
        const n = Number(String(emp.rateEur).replace(',', '.'));
        return { ...emp, rateEur: Number.isFinite(n) ? n : null };
      }),
    };
    setImporting(true);
    setError(null);
    setReport(null);
    try {
      const r = await migrationService.importPayload(cleanPayload, { dryRun, billingMode });
      setReport(r);
      setNotice(dryRun ? 'Dry run finished — nothing was written. Review the report below.' : 'Import finished.');
      refreshBatches();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const totals = payload && payload.meta ? payload.meta.totals : null;
  const allWarnings = payload
    ? [
        ...payload.warnings.map((w) => `[${w.where}] ${w.message}`),
        ...payload.customers.flatMap((c) => c.warnings.map((w) => `${c.name}: ${w}`)),
        ...payload.employees.flatMap((emp) => emp.warnings.map((w) => `${emp.name}: ${w}`)),
      ]
    : [];

  const custName = payload ? new Map(payload.customers.map((c) => [c.key, c.name])) : new Map();
  const hoursByEmp = new Map();
  if (payload) {
    for (const h of payload.hours) {
      hoursByEmp.set(h.employeeKey, (hoursByEmp.get(h.employeeKey) || 0) + h.minutes);
    }
  }

  return (
    <Wrap>
      <Intro>
        Go-live migration from the operational A26 Excel workbook (&quot;Adatbázis&quot;). Upload the
        workbook, verify and correct the interpreted data below, download the review CSVs, then
        import — always dry-run first. Re-uploading a newer version of the workbook later only
        applies the differences (new customers, employees and hours), so the weekly delta update
        during the customer-by-customer cutover uses this same page.
      </Intro>

      <Row>
        <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleFile} />
        <Button variant="primary" onClick={() => fileRef.current && fileRef.current.click()} disabled={parsing} icon={<FiUploadCloud />}>
          {parsing ? 'Parsing…' : 'Select & parse A26 Excel'}
        </Button>
        {batchId && (
          <>
            {CSV_KINDS.map((kind) => (
              <Button
                key={kind}
                variant="outline"
                size="sm"
                icon={<FiDownload />}
                onClick={() => migrationService.downloadCsv(batchId, kind).catch((err) => setError(`CSV download failed: ${err.message}`))}
              >
                {kind}.csv
              </Button>
            ))}
          </>
        )}
      </Row>

      {error && <Alert $tone="error">{error}</Alert>}
      {notice && !error && <Alert>{notice}</Alert>}

      {totals && (
        <StatRow>
          <Stat><b>{totals.customers}</b> customers</Stat>
          <Stat><b>{totals.employees}</b> employees</Stat>
          <Stat><b>{totals.employeesInactive}</b> marked stopped</Stat>
          <Stat><b>{totals.hourRows}</b> week rows</Stat>
          <Stat><b>{totals.hourSum}</b> hours total</Stat>
          <Stat><b>{totals.pipelineRows}</b> pipeline rows (saved, not imported)</Stat>
          <Stat><b>{allWarnings.length}</b> warnings</Stat>
        </StatRow>
      )}

      {payload && (
        <>
          <SectionTitle>Customers</SectionTitle>
          <Note>
            CVR, e-mail, phone and active are editable — corrections here are what gets imported.
            Unticking &quot;import&quot; skips the customer (and its assignments/hours).
          </Note>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Import</th><th>Name</th><th>CVR</th><th>Country</th><th>Contact</th>
                  <th>E-mail</th><th>Phone</th><th>Active</th><th>Warnings</th>
                </tr>
              </thead>
              <tbody>
                {payload.customers.map((c) => (
                  <tr key={c.key}>
                    <td><input type="checkbox" aria-label={`Import ${c.name}`} checked={c.include !== false} onChange={(e) => patchCustomer(c.key, 'include', e.target.checked)} /></td>
                    <td>{c.name}</td>
                    <td><Cellput $w="90px" aria-label={`${c.name} CVR`} value={c.cvr || ''} placeholder="CVR" onChange={(e) => patchCustomer(c.key, 'cvr', e.target.value)} /></td>
                    <td>{c.country}</td>
                    <td>{c.contactName || '—'}</td>
                    <td><Cellput $w="200px" aria-label={`${c.name} e-mail`} value={c.contactEmail || ''} onChange={(e) => patchCustomer(c.key, 'contactEmail', e.target.value)} /></td>
                    <td><Cellput $w="120px" aria-label={`${c.name} phone`} value={c.phone || ''} onChange={(e) => patchCustomer(c.key, 'phone', e.target.value)} /></td>
                    <td><input type="checkbox" aria-label={`${c.name} active`} checked={c.active !== false} onChange={(e) => patchCustomer(c.key, 'active', e.target.checked)} /></td>
                    <td>{c.warnings.length ? <Pill $tone="warn">{c.warnings.join(' | ')}</Pill> : <Pill $tone="ok">ok</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>

          <SectionTitle>Employees</SectionTitle>
          <Note>
            Status was auto-detected from the sheet (Felmondott / fired / last working day …) — the
            evidence is shown, please verify. Rate is EUR per hour (informational Bruttoløn). No
            e-mail means a non-deliverable placeholder login is created; fill in real addresses when
            the employee should log in to Tempus.
          </Note>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Import</th><th>Name</th><th>Active</th><th>Stop evidence</th><th>E-mail</th>
                  <th>Phone</th><th>Rate EUR</th><th>Start</th><th>Hours</th><th>Customer(s)</th><th>Warnings</th>
                </tr>
              </thead>
              <tbody>
                {payload.employees.map((emp) => (
                  <tr key={emp.key}>
                    <td><input type="checkbox" aria-label={`Import ${emp.name}`} checked={emp.include !== false} onChange={(e) => patchEmployee(emp.key, 'include', e.target.checked)} /></td>
                    <td>{emp.name}</td>
                    <td><input type="checkbox" aria-label={`${emp.name} active`} checked={emp.active !== false} onChange={(e) => patchEmployee(emp.key, 'active', e.target.checked)} /></td>
                    <td style={{ whiteSpace: 'normal', maxWidth: 260 }}>{emp.stopEvidence || ''}</td>
                    <td><Cellput $w="210px" aria-label={`${emp.name} e-mail`} value={emp.email || ''} onChange={(e) => patchEmployee(emp.key, 'email', e.target.value || null)} /></td>
                    <td><Cellput $w="120px" aria-label={`${emp.name} phone`} value={emp.phone || ''} onChange={(e) => patchEmployee(emp.key, 'phone', e.target.value || null)} /></td>
                    {/* edited as raw text so "12,5" can be typed; normalized on import */}
                    <td><Cellput $w="60px" aria-label={`${emp.name} rate EUR`} value={emp.rateEur == null ? '' : String(emp.rateEur)} onChange={(e) => patchEmployee(emp.key, 'rateEur', e.target.value === '' ? null : e.target.value)} /></td>
                    <td><Cellput $w="110px" type="date" aria-label={`${emp.name} start date`} value={emp.startDate || ''} onChange={(e) => patchEmployee(emp.key, 'startDate', e.target.value || null)} /></td>
                    <td>{toHmm(hoursByEmp.get(emp.key) || 0)}</td>
                    <td>{emp.assignments.map((a) => custName.get(a.customerKey) || a.customerKey).join(', ')}</td>
                    <td>{emp.warnings.length ? <Pill $tone="warn">{emp.warnings.join(' | ')}</Pill> : <Pill $tone="ok">ok</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>

          {allWarnings.length > 0 && (
            <>
              <SectionTitle>Warnings ({allWarnings.length})</SectionTitle>
              <WarnList>
                {allWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </WarnList>
            </>
          )}

          <SectionTitle>Import</SectionTitle>
          <Row>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry run (validate everything, write nothing)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Billing:
              <select value={billingMode} onChange={(e) => setBillingMode(e.target.value)}>
                <option value="historic-all">historic-all — imported hours already invoiced/paid outside Tempus (recommended)</option>
                <option value="from-colors">from-colors — mark invoiced only where the Excel color said so</option>
                <option value="none">none — Tempus will invoice and pay everything imported</option>
              </select>
            </label>
            <Button variant={dryRun ? 'primary' : 'accent'} onClick={runImport} disabled={importing} icon={<FiPlay />}>
              {importing ? 'Running…' : dryRun ? 'Run dry-run import' : 'IMPORT to this Tempus'}
            </Button>
          </Row>
          <Note>
            &quot;historic-all&quot; keeps Tempus from re-invoicing or re-paying the imported history.
            During the cutover period the Excel remains the billing source for its customers, so this
            is the safe default for every delta run.
          </Note>
        </>
      )}

      {report && (
        <>
          <SectionTitle>{report.dryRun ? 'Dry-run report (rolled back)' : 'Import report'}</SectionTitle>
          <StatRow>
            {Object.entries(report.summary || {}).map(([k, v]) => (
              <Stat key={k}><b>{String(v)}</b>{k}</Stat>
            ))}
          </StatRow>
          {report.hours && report.hours.conflicts && report.hours.conflicts.length > 0 && (
            <>
              <SectionTitle><FiAlertTriangle /> Conflicts (Tempus data kept)</SectionTitle>
              <WarnList>
                {report.hours.conflicts.map((c, i) => (
                  <li key={i}>{c.date} — {c.employee}: {c.reason} (Tempus {c.tempusMinutes} min, Excel {c.excelMinutes} min)</li>
                ))}
              </WarnList>
            </>
          )}
          {report.warnings && report.warnings.length > 0 && (
            <WarnList>
              {report.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </WarnList>
          )}
          <SectionTitle>Payroll verification (per employee per month)</SectionTitle>
          <Note>
            Compares the Excel week totals with what Tempus payroll will see (sum of imported
            day-entries per calendar month — the same basis as Admin → Salary). Every row should be
            a match; conflicts above explain any difference.
          </Note>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Employee</th><th>Month</th><th>Excel</th><th>Tempus (imported)</th>
                  <th>Tempus (other entries)</th><th>Match</th><th>Rate EUR</th><th>Gross EUR</th><th>Bruttoløn DKK</th>
                </tr>
              </thead>
              <tbody>
                {(report.verification || []).map((v, i) => (
                  <tr key={i}>
                    <td>{v.employee}</td>
                    <td>{v.month}</td>
                    <td>{toHmm(v.excelMinutes)}</td>
                    <td>{toHmm(v.tempusImportedMinutes)}</td>
                    <td>{v.tempusOtherMinutes ? toHmm(v.tempusOtherMinutes) : '—'}</td>
                    <td>{v.match ? <Pill $tone="ok"><FiCheck /> match</Pill> : <Pill $tone="bad"><FiX /> differs</Pill>}</td>
                    <td>{v.rateEur == null ? '—' : v.rateEur}</td>
                    <td>{v.grossEur == null ? '—' : v.grossEur}</td>
                    <td>{v.bruttoDkk == null ? '—' : v.bruttoDkk}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}

      {batches.length > 0 && (
        <>
          <SectionTitle>Previous runs</SectionTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr><th>#</th><th>Kind</th><th>File</th><th>When</th><th>Options</th><th>Summary</th></tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id}>
                    <td>{b.id}</td>
                    <td>{b.kind}{b.options && b.options.dryRun === false ? '' : b.kind === 'import' ? ' (dry)' : ''}</td>
                    <td>{b.filename || '—'}</td>
                    <td>{new Date(b.createdAt).toLocaleString()}</td>
                    <td>{b.options ? `${b.options.billingMode || ''}` : '—'}</td>
                    <td style={{ whiteSpace: 'normal', maxWidth: 420 }}>
                      {b.summary ? Object.values(b.summary).join(' · ') : b.totals ? `${b.totals.customers} customers, ${b.totals.employees} employees, ${b.totals.hourSum} h` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}
    </Wrap>
  );
};

export default MigrationPanel;
